import React, { FC, useEffect, useMemo, useState } from 'react';
import { ReactMultiEmail, isEmail } from 'react-multi-email';
import { Box, FormControl, FormHelperText } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import translate from '@translate';
import IconButton from 'src/components/IconButton';
import { colors, sizes } from 'src/themes/default';

export interface MultiEmailFieldPropsType {
    value?: string | null;
    onChange?: (value: string, error: boolean) => void;
    error?: boolean;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    label?: React.ReactNode;
    name?: string;
}

type MultiEmailContainerProps = {
    hasError?: boolean;
    disabled?: boolean;
};

const MultiEmailContainer = styled('div', {
    shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'disabled',
})<MultiEmailContainerProps>(({ hasError, disabled }) => ({
    '& .react-multi-email': {
        minHeight: '56px',
        borderRadius: sizes.radius,
        border: `${sizes.border} solid ${hasError ? colors.alert_O : colors.text_T3}`,
        padding: '6px 10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        backgroundColor: colors.background_W,
        ...(disabled
            ? {
                  opacity: 0.6,
                  pointerEvents: 'none',
              }
            : {}),
    },
    '& .react-multi-email input': {
        border: 'none',
        outline: 'none',
        flex: 1,
        minWidth: '120px',
        fontSize: sizes.body_FS,
        lineHeight: sizes.body_LH,
        padding: '6px 4px',
        fontFamily: 'inherit',
        color: colors.text_T1,
        backgroundColor: 'transparent',
    },
    '& [data-tag]': {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 6px',
        marginRight: '6px',
        marginBottom: '4px',
        borderRadius: sizes.radius,
        backgroundColor: colors.background_B1,
        color: colors.text_T1,
        fontSize: sizes.small_FS,
        lineHeight: sizes.small_LH,
    },
    '& [data-tag-handle]': {
        cursor: 'pointer',
        marginLeft: '6px',
        fontSize: '14px',
        color: colors.text_T2,
        display: 'inline-flex',
        alignItems: 'center',
    },
}));

const getEmailList = (value?: string | null): string[] => {
    if (!value) {
        return [];
    }
    return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
};

const formatEmails = (emails: string[]): string => {
    return emails.join(', ');
};

const MultiEmailField: FC<MultiEmailFieldPropsType> = (props) => {
    const [emails, setEmails] = useState<string[]>(() => getEmailList(props.value));

    useEffect(() => {
        setEmails(getEmailList(props.value));
    }, [props.value]);

    const errorText = useMemo(() => {
        if (!props.error) {
            return ' ';
        }
        if (props.required && !emails.length) {
            return translate('textField.requiredError');
        }
        return translate('textField.error');
    }, [props.error, props.required, emails.length]);

    const handleChange = (newEmails: string[]) => {
        setEmails(newEmails);
        if (props.onChange) {
            const hasError = !!props.required && newEmails.length === 0;
            props.onChange(formatEmails(newEmails), hasError);
        }
    };

    const handleCopy = async () => {
        const text = formatEmails(emails);
        if (!text || !navigator?.clipboard) {
            return;
        }
        await navigator.clipboard.writeText(text);
    };

    return (
        <FormControl
            fullWidth={props.fullWidth}
            error={props.error}
            disabled={props.disabled}
            sx={{ paddingTop: sizes.border, paddingBottom: sizes.border }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginLeft: sizes.input_label_ML,
                    fontSize: sizes.small_FS,
                    lineHeight: sizes.small_LH,
                    fontWeight: sizes.small_W,
                    color: colors.text_T2,
                }}
            >
                <label>
                    {props.label}
                    {props.required && (
                        <span style={{ color: colors.primary_O, marginLeft: '2px' }}>*</span>
                    )}
                </label>
                <IconButton
                    size="small"
                    tooltiop="Kopírovat emaily"
                    onClick={handleCopy}
                    disabled={props.disabled || !emails.length}
                    sx={{ padding: '2px' }}
                >
                    <ContentCopyIcon sx={{ fontSize: '16px' }} />
                </IconButton>
            </Box>
            <MultiEmailContainer hasError={props.error} disabled={props.disabled}>
                <ReactMultiEmail
                    emails={emails}
                    onChange={handleChange}
                    validateEmail={(email) => isEmail(email)}
                    getLabel={(email, index, removeEmail) => (
                        <span data-tag key={index}>
                            {email}
                            <span
                                data-tag-handle
                                onClick={() => {
                                    if (!props.disabled) {
                                        removeEmail(index);
                                    }
                                }}
                            >
                                ×
                            </span>
                        </span>
                    )}
                />
            </MultiEmailContainer>
            <FormHelperText>{errorText}</FormHelperText>
        </FormControl>
    );
};

export default MultiEmailField;
